import { toNumberOr } from './contracts.js'

const toNumber = (value, fallback = 0) => toNumberOr(value, fallback)

const normalizeTicketPrice = (lottery, fallbackTicketPrice = 0) => {
  return toNumber(
    lottery?.ticket_price
      ?? lottery?.price_per_ticket
      ?? lottery?.unit_price
      ?? fallbackTicketPrice,
    fallbackTicketPrice,
  )
}

const normalizeSold = (lottery) => {
  return toNumber(
    lottery?.sold_tickets
      ?? lottery?.sold_count
      ?? lottery?.tickets_sold
      ?? lottery?.sales?.sold
      ?? 0,
    0,
  )
}

const normalizeReserved = (lottery) => {
  return toNumber(
    lottery?.reserved_tickets
      ?? lottery?.reserved_count
      ?? lottery?.tickets_reserved
      ?? lottery?.sales?.reserved
      ?? 0,
    0,
  )
}

const normalizeRefunded = (lottery) => {
  return toNumber(
    lottery?.refunded_tickets
      ?? lottery?.refunded_count
      ?? lottery?.tickets_refunded
      ?? lottery?.sales?.refunded
      ?? 0,
    0,
  )
}

const normalizeVoided = (lottery) => {
  return toNumber(
    lottery?.voided_tickets
      ?? lottery?.voided_count
      ?? lottery?.tickets_voided
      ?? lottery?.sales?.voided
      ?? 0,
    0,
  )
}

const normalizeTotal = (lottery) => {
  return toNumber(
    lottery?.total_tickets
      ?? lottery?.tickets_total
      ?? lottery?.ticket_count
      ?? lottery?.tickets_per_batch
      ?? lottery?.sales?.total
      ?? 0,
    0,
  )
}

export const summarizeLotterySales = (lottery, options = {}) => {
  const ticketPrice = normalizeTicketPrice(lottery, toNumber(options.ticketPrice, 0))
  const totalTickets = normalizeTotal(lottery)
  const soldTickets = normalizeSold(lottery)
  const reservedTickets = normalizeReserved(lottery)
  const refundedTickets = normalizeRefunded(lottery)
  const voidedTickets = normalizeVoided(lottery)
  const accountedTickets = soldTickets + reservedTickets + refundedTickets + voidedTickets

  const mismatchCount = Math.max(0, accountedTickets - totalTickets)
  const estimatedRevenue = Math.max(0, soldTickets - refundedTickets - voidedTickets) * ticketPrice

  const warnings = []
  if (mismatchCount > 0) {
    warnings.push(`Reconciliation mismatch (${mismatchCount}) for lottery #${lottery?.id ?? 'unknown'}.`)
  }
  if (lottery?.partial === true || lottery?.degraded === true) {
    warnings.push('Partial/degraded lottery sales payload detected.')
  }

  const soldRatio = totalTickets > 0 ? soldTickets / totalTickets : 0
  const reservedRatio = totalTickets > 0 ? reservedTickets / totalTickets : 0

  return {
    lotteryId: Number(lottery?.id ?? lottery?.lottery_id ?? 0),
    orgId: Number(lottery?.organization_id ?? 0),
    subOrgId: Number(lottery?.sub_org_id ?? 0),
    name: String(lottery?.name ?? ''),
    lifecycle: String(lottery?.status ?? lottery?.lifecycle ?? 'unknown').toLowerCase(),
    ticketPrice,
    totalTickets,
    soldTickets,
    reservedTickets,
    refundedTickets,
    voidedTickets,
    estimatedRevenue,
    soldRatio,
    reservedRatio,
    mismatchCount,
    hasMismatch: mismatchCount > 0,
    partial: lottery?.partial === true,
    degraded: lottery?.degraded === true,
    warnings,
  }
}

export const summarizeSubOrgSales = (lotteries, options = {}) => {
  const summaries = (Array.isArray(lotteries) ? lotteries : []).map((lottery) => summarizeLotterySales(lottery, options))
  const grouped = new Map()

  for (const entry of summaries) {
    const key = String(entry.subOrgId || 'unscoped')
    const current = grouped.get(key) || {
      subOrgId: entry.subOrgId || null,
      totalTickets: 0,
      soldTickets: 0,
      reservedTickets: 0,
      refundedTickets: 0,
      voidedTickets: 0,
      estimatedRevenue: 0,
      mismatchCount: 0,
      partial: false,
      degraded: false,
      lotteries: 0,
    }

    current.totalTickets += entry.totalTickets
    current.soldTickets += entry.soldTickets
    current.reservedTickets += entry.reservedTickets
    current.refundedTickets += entry.refundedTickets
    current.voidedTickets += entry.voidedTickets
    current.estimatedRevenue += entry.estimatedRevenue
    current.mismatchCount += entry.mismatchCount
    current.partial = current.partial || entry.partial
    current.degraded = current.degraded || entry.degraded
    current.lotteries += 1
    grouped.set(key, current)
  }

  return [...grouped.values()].map((row) => ({
    ...row,
    hasMismatch: row.mismatchCount > 0,
    warnings: [
      ...(row.mismatchCount > 0 ? [`Sub-organization mismatch count: ${row.mismatchCount}.`] : []),
      ...(row.partial || row.degraded ? ['Sub-organization totals include partial/degraded data.'] : []),
    ],
  }))
}

export const summarizeOrgSales = (lotteries, options = {}) => {
  const summaries = (Array.isArray(lotteries) ? lotteries : []).map((lottery) => summarizeLotterySales(lottery, options))
  const grouped = new Map()

  for (const entry of summaries) {
    const key = String(entry.orgId || 'unscoped')
    const current = grouped.get(key) || {
      orgId: entry.orgId || null,
      totalTickets: 0,
      soldTickets: 0,
      reservedTickets: 0,
      refundedTickets: 0,
      voidedTickets: 0,
      estimatedRevenue: 0,
      mismatchCount: 0,
      partial: false,
      degraded: false,
      lotteries: 0,
    }

    current.totalTickets += entry.totalTickets
    current.soldTickets += entry.soldTickets
    current.reservedTickets += entry.reservedTickets
    current.refundedTickets += entry.refundedTickets
    current.voidedTickets += entry.voidedTickets
    current.estimatedRevenue += entry.estimatedRevenue
    current.mismatchCount += entry.mismatchCount
    current.partial = current.partial || entry.partial
    current.degraded = current.degraded || entry.degraded
    current.lotteries += 1
    grouped.set(key, current)
  }

  return [...grouped.values()].map((row) => ({
    ...row,
    hasMismatch: row.mismatchCount > 0,
    warnings: [
      ...(row.mismatchCount > 0 ? [`Organization mismatch count: ${row.mismatchCount}.`] : []),
      ...(row.partial || row.degraded ? ['Organization totals include partial/degraded data.'] : []),
    ],
  }))
}

export const getReconciliationIndicators = (summary) => {
  const mismatchCount = toNumber(summary?.mismatchCount, 0)
  const partial = summary?.partial === true
  const degraded = summary?.degraded === true

  return {
    ok: mismatchCount === 0 && !partial && !degraded,
    hasMismatch: mismatchCount > 0,
    partial,
    degraded,
    label: mismatchCount > 0
      ? 'Mismatch detected'
      : partial || degraded
        ? 'Partial/degraded'
        : 'Reconciled',
  }
}

